import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import PlusIcon from "../../Images/PlusIcon.svg";
import { API_URL } from "../../config/api";

const HIDE_CREATE_ROLES = ["super_admin"];

const ProgramSchedule = () => {
  const { programsData } = useOutletContext();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  useEffect(() => {
    const fetchRole = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setRoleLoaded(true); // ✅ no token, still mark as loaded
          return;
        }
        const res = await fetch(`${API_URL}/current_user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCurrentUserRole(data?.roles?.[0]?.name || null);
      } catch (err) {
        console.error("Failed to fetch role:", err);
      } finally {
        setRoleLoaded(true); // ✅ always mark loaded after fetch
      }
    };
    fetchRole();
  }, []);

  // ✅ Don't show button until role is confirmed
  const showCreateButton =
    roleLoaded && !HIDE_CREATE_ROLES.includes(currentUserRole);

  if (!programsData) {
    return (
      <div className="p-10 text-center text-gray-400 text-lg">
        Loading program schedule...
      </div>
    );
  }

  const scheduledPrograms = Array.isArray(programsData?.scheduled_programs_list)
    ? programsData.scheduled_programs_list
    : [];

  const monthsData = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ].map((name, index) => ({ name, index }));

  const generateDatesForMonth = () => {
    const year = selectedDate.getFullYear();
    const daysInMonth = new Date(year, selectedMonth + 1, 0).getDate();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(year, selectedMonth, i + 1);
      return {
        date: i + 1,
        day: dayNames[date.getDay()],
        active:
          selectedMonth === selectedDate.getMonth() &&
          i + 1 === selectedDate.getDate(),
      };
    });
  };

  const dates = generateDatesForMonth();

  const handleMonthChange = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setSelectedDate(new Date(selectedDate.getFullYear(), monthIndex, 1));
  };

  const formatSelectedDate = () => {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = dayNames[selectedDate.getDay()];
    const month = monthNames[selectedDate.getMonth()];
    const date = String(selectedDate.getDate()).padStart(2, "0");
    return `${day}, ${month} ${date} ${selectedDate.getFullYear()}`;
  };

  const isScheduleOnDate = (schedule, checkDate) => {
    const [startYear, startMonth, startDay] = schedule.start_date
      .split("-")
      .map(Number);
    const [endYear, endMonth, endDay] = schedule.end_date
      .split("-")
      .map(Number);

    const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0);
    const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);

    const selected = new Date(checkDate);
    selected.setHours(12, 0, 0, 0);

    return startDate <= selected && selected <= endDate;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}.${minutes} ${ampm}`;
  };

  const filteredSchedules = scheduledPrograms.filter((schedule) =>
    isScheduleOnDate(schedule, selectedDate),
  );

  return (
    <div className="w-full flex flex-col gap-6 p-6 bg-gray-50">
      {/* TOP BAR */}
      <div className="w-full flex items-center justify-between">
        <h2 className="text-zinc-800 text-2xl font-bold">Program Schedule</h2>
        {/* ✅ Hide for super_admin, org_admin, training_partner_admin */}
        {showCreateButton && (
          <button
            className="px-4 py-2 flex bg-[#4AACC5] hover:bg-[#75cee4] text-white text-sm font-semibold rounded-lg items-center gap-2 shadow-sm"
            onClick={() => navigate("/program_schdule/create_schdule")}
          >
            <img src={PlusIcon} alt="" className="w-4 h-4" />
            Create Schedule
          </button>
        )}
      </div>

      {/* CALENDAR */}
      <div className="w-full bg-white rounded-lg border shadow-sm">
        {/* Month selector */}
        <div className="flex items-center justify-between px-12 py-6 border-b">
          {monthsData.map((month) => (
            <span
              key={month.index}
              onClick={() => handleMonthChange(month.index)}
              className={`text-sm cursor-pointer transition-colors ${
                month.index === selectedMonth
                  ? "text-zinc-800 font-semibold text-base"
                  : "text-stone-300 hover:text-stone-400"
              }`}
            >
              {month.name}
            </span>
          ))}
        </div>

        {/* Date strip */}
        <div className="overflow-x-auto px-12 py-4">
          <div className="flex gap-4 w-max">
            {dates.map((d, i) => (
              <div
                key={i}
                className="flex flex-col items-center cursor-pointer"
                onClick={() =>
                  setSelectedDate(
                    new Date(selectedDate.getFullYear(), selectedMonth, d.date),
                  )
                }
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    d.active ? "bg-[#4AACC5]" : "bg-indigo-50"
                  }`}
                >
                  <span
                    className={`text-sm font-medium ${
                      d.active ? "text-white" : "text-neutral-500"
                    }`}
                  >
                    {d.date}
                  </span>
                </div>
                <span
                  className={`text-xs mt-1 ${
                    d.active ? "text-zinc-800 font-medium" : "text-stone-300"
                  }`}
                >
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-start px-12 pb-5 text-zinc-800 text-lg font-semibold">
          {formatSelectedDate()}
        </div>
      </div>

      {/* SCHEDULE CARDS */}
      <div className="w-full flex flex-col gap-3">
        {filteredSchedules.length > 0 ? (
          filteredSchedules.map((schedule, index) => {
            const course = schedule.course || {};
            const instructors = schedule.instructors || [];
            const enrollments = schedule.enrollments || [];

            return (
              <div
                key={`${schedule.schedule_id}-${index}`}
                className="w-full bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-5 hover:shadow-md transition-shadow"
              >
                {/* Row 1: Title + View Details */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-zinc-800 text-base font-bold">
                    {course.title || "Untitled Course"}
                  </h3>
                  <button
                    className="text-[#4AACC5] text-sm font-medium hover:underline whitespace-nowrap ml-6"
                    onClick={() =>
                      console.log("View details:", schedule.schedule_id)
                    }
                  >
                    View Details
                  </button>
                </div>

                {/* Row 2: Instructor */}
                {instructors.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <svg
                      className="w-3.5 h-3.5 text-[#4AACC5] flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <span className="text-gray-500 text-sm">
                      {instructors
                        .map((inst) => inst.name || inst.email)
                        .join(", ")}
                      , Instructor
                    </span>
                  </div>
                )}

                {/* Row 3: Time */}
                {schedule.start_time && schedule.end_time && (
                  <div className="flex items-center gap-1.5 mb-4">
                    <svg
                      className="w-3.5 h-3.5 text-[#4AACC5] flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
                      />
                    </svg>
                    <span className="text-gray-500 text-sm">
                      Time : {formatTime(schedule.start_time)} to{" "}
                      {formatTime(schedule.end_time)}
                    </span>
                  </div>
                )}

                {/* Row 4: Students (left) + Location (right) */}
                <div className="flex items-center justify-between">
                  {/* Students */}
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {enrollments.slice(0, 5).map((e, i) => (
                        <img
                          key={i}
                          src={
                            e.student?.avatar_url ||
                            `https://placehold.co/28x28`
                          }
                          className="w-7 h-7 rounded-full border-2 border-white object-cover"
                          alt="Student"
                        />
                      ))}
                    </div>
                    <span className="text-gray-500 text-sm">
                      {enrollments.length}{" "}
                      {enrollments.length === 1 ? "Student" : "Students"}
                    </span>
                  </div>

                  {/* Location */}
                  {schedule.location && (
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-3.5 h-3.5 text-[#4AACC5] flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="text-gray-500 text-sm">
                        {schedule.location}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="w-full bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center justify-center gap-3">
            <svg
              className="w-10 h-10 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"
              />
            </svg>
            <p className="text-gray-400 text-sm">
              No programs scheduled on this date
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramSchedule;
